/* eslint-disable no-underscore-dangle, camelcase */
import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  useNavigate,
} from 'react-router';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import i18n from 'i18next';
import {
  toast,
} from 'react-toastify';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  useProgram,
} from 'web-hooks';
import {
  uiActions,
  commonActions,
} from 'web-actions';

import {
  Button,
  Author,
  Tooltip,
  MoveToFolder,
  CreatableEntityButton,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

import * as S from './styled';

type Props = {
  mode?: 'all' | 'standard',
  readOnly: boolean,
  programId: number,
  resourceType: 'programTemplates' | 'personalPrograms',
};

const ProgramHeader = ({
  mode = 'standard',
  readOnly,
  programId,
  resourceType,
}: Props) => {
  const navigate = useNavigate();
  const isPersonal = resourceType === 'personalPrograms';
  const [isSaveModal, setSaveModal] = useState(false);
  const mapState = useCallback((state) => {
    const program = coreSelectors.getResourceById(
      resourceType,
      programId,
    )(state);
    return {
      isPro: coreSelectors.getProfile('is_pro')(state),
      program,
      programAuthor: coreSelectors.getResourceById(
        'clients',
        program?.author_id,
      )(state),
      programWorkouts: coreSelectors.getResourceMappedList(
        'programWorkouts',
        `program_${programId}`,
      )(state),
      workoutOptions: coreSelectors.getProgamWorkoutOptions(
        isPersonal ? program.person_id : 'me',
      )(state),
      weeks: coreSelectors.getProgamWeeks(
        programId,
        resourceType,
      )(state).filter(item => item.id > 0),
    };
  }, [programId]);

  const {
    isPro,
    weeks,
    program,
    programAuthor,
    workoutOptions,
    programWorkouts,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const {
    requestAddWeek,
    requestAddWorkout,
    requestSaveAsTemplate,
  } = useProgram({ programId, resourceType });

  useEffect(() => {
    if (program?.person_id) {
      dispatch(
        coreActions.requestClientPersonalEntity({
          qs: {
            status: 'active',
          },
          clientId: program.person_id,
          resourceType: 'personalWorkouts',
        }),
      );
    }
  }, [program?.person_id]);

  const handlePrint = () => {
    window.print();
    dispatch(commonActions.triggerAnalytic('clicked_print_button'));
  };

  const handleEdit = () => {
    if (readOnly) {
      const onConfirm = () => {
        navigate(`/personal_programs/${program.id}/edit/workouts`);
      };

      dispatch(
        uiActions.confirmModal({
          modal: {
            title: `Edit ${program?.name}`,
            bodyText: 'Changes will be saved automatically.',
            confirmText: 'Update',
          },
          onConfirm,
        }),
      );
    } else {
      navigate(`/personal_programs/${program.id}/workouts`);
    }
  };

  return (
    <S.Header>
      <Flex
        align="center"
        spacing={30}
        grow={1}
      >
        {readOnly ? (
          <S.CounterWrapper align="center" spacing={10}>
            <S.Counter>
              {weeks.length || 0}
            </S.Counter>
            <S.CounterName>WEEKS</S.CounterName>
          </S.CounterWrapper>
        ) : (
          <S.CounterWrapper justify="center" align="center" spacing={10}>
            <S.Counter>
              {weeks.length || 0}
            </S.Counter>
            <S.CounterName>WEEKS</S.CounterName>
            <Tooltip
              body={i18n.t(`tooltip.program.${mode === 'standard'
                ? 'add.weeks'
                : 'standard.view'}`)}
              positions={['right']}
            >
              <S.AddButton
                role="button"
                onClick={requestAddWeek}
                style={{
                  cursor: mode === 'all' ? 'default' : 'pointer',
                }}
              >
                <S.PlusCircle />
              </S.AddButton>
            </Tooltip>
          </S.CounterWrapper>
        )}
        <S.CounterWrapper
          align="center"
          spacing={10}
          grow={1}
        >
          <S.Counter>
            {programWorkouts.length}
          </S.Counter>
          <S.CounterName>{i18n.t('workouts')}</S.CounterName>
          {!readOnly && (
            <CreatableEntityButton
              buttonProps={{
                appearance: 'empty',
                style: {
                  cursor: mode === 'all' ? 'default' : 'pointer',
                  opacity: 0.6,
                  padding: 0,
                  marginTop: 2,
                },
              }}
              options={workoutOptions}
              buttonLabel={(
                <S.PlusCircle />
              )}
              placeholder={i18n.t('search.placeholder.workoutTemplates')}
              isDisabled={mode === 'all'}
              tooltip={i18n.t(`tooltip.program.${mode === 'standard'
                ? 'add.workouts'
                : 'standard.view'}`)}
              menuPlacement="bottom"
              onRequestAdd={requestAddWorkout}
              onRequestAddNew={requestAddWorkout}
            />
          )}
        </S.CounterWrapper>
      </Flex>
      {isPersonal ? (
        <Flex
          align="center"
          spacing={10}
          noPrint
        >
          {readOnly && (
            <Button
              ghost
              appearance="primary"
              onClick={handlePrint}
            >
              Print
            </Button>
          )}
          {isPro && (
            <>
              <Button
                ghost
                appearance="primary"
                onClick={handleEdit}
              >
                {readOnly ? 'Edit' : 'Done'}
              </Button>
              <Button
                ghost
                appearance="primary"
                onClick={() => setSaveModal(true)}
              >
                Save as Template
              </Button>
            </>
          )}
        </Flex>
      ) : (
        <Author
          fullName={program?.author_full_name}
          avatar={programAuthor?.user_profile?.avatar?.thumb?.url}
        />
      )}
      <MoveToFolder
        modalState={{
          isOpen: isSaveModal,
          title: 'Save to Folder',
          helper: 'Select folder to save Program template to',
          buttonText: 'Save',
          folderName: 'Program Templates',
        }}
        onSubmit={(values, actions) => {
          const callbacks = {
            onSuccess: () => {
              actions.setSubmitting(false);
              setSaveModal(false);
              toast.success(
                'Program was saved to your library',
              );
            },
          };
          requestSaveAsTemplate(values, callbacks);
        }}
        onRequestClose={() => setSaveModal(false)}
      />
    </S.Header>
  );
};

export default ProgramHeader;
